/**
 * Centralized analytics tracking — funnel + engagement events.
 *
 * Today: Vercel Analytics only (already injected in main.ts). To enable
 * PostHog later: install `posthog-js`, init it in main.ts with your
 * project key, and uncomment the posthog branch in `track()`. No event
 * call sites change.
 *
 * Funnel-specific helpers (`trackDemoStarted`, `trackPlayStarted`, etc.)
 * use sessionStorage so derived signals like "did the user play the
 * demo before clicking PLAY" stay accurate across scene transitions.
 *
 * All calls are fire-and-forget — analytics MUST NEVER block UX, so
 * every dispatch is wrapped in try/catch.
 */

import { track as vercelTrack } from '@vercel/analytics';

const SESSION_KEY = 'pms_analytics_session';

interface SessionState {
  /** Unix ms when TRY DEMO was clicked. Persists until tab closes. */
  demoStartedAt?: number;
  /** Unix ms when the demo ended (death / back-to-menu / extracted). */
  demoEndedAt?: number;
  /** How many times the user clicked PLAY this session. */
  playClickCount?: number;
  /** How many paid plays actually started (payment confirmed + match joined). */
  paidPlayCount?: number;
}

function getSession(): SessionState {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_KEY) || '{}');
  } catch {
    return {};
  }
}

function setSession(patch: Partial<SessionState>): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ...getSession(), ...patch }));
  } catch {
    /* sessionStorage blocked — analytics signals degrade but app keeps working */
  }
}

/** Fire a tracked event to every enabled analytics provider. Safe to
 *  call from any code path; failures are swallowed. */
export function track(event: string, props?: Record<string, any>): void {
  try {
    vercelTrack(event, props);
  } catch {
    /* Vercel script not loaded yet, etc. */
  }
  // PostHog — uncomment after `npm i posthog-js` + init in main.ts.
  // try { (window as any).posthog?.capture(event, props); } catch {}
}

// ============================================================================
// Funnel helpers — call these at conversion points instead of bare `track()`
// when you need derived signals (after_demo, paid_play_count, etc.).
// ============================================================================

/** Fired when user clicks TRY DEMO. Marks the session for downstream
 *  events that care whether the user came from a demo. */
export function trackDemoStarted(): void {
  setSession({ demoStartedAt: Date.now() });
  track('demo_started');
}

/** Fired when the demo session ends (death / extraction / back to menu).
 *  Includes how long the user actually played the demo so we can see if
 *  they bounced after 5 seconds vs played a full match. */
export function trackDemoEnded(reason: 'died' | 'extracted' | 'back_to_menu'): void {
  const session = getSession();
  if (!session.demoStartedAt || session.demoEndedAt) return; // dedupe
  const duration_ms = Date.now() - session.demoStartedAt;
  setSession({ demoEndedAt: Date.now() });
  track('demo_ended', { reason, duration_ms });
}

/** Fired when user clicks the PLAY button (before any wallet/payment
 *  step). Captures whether they previously played the demo this session
 *  and how many times they've clicked PLAY total — the key signal for
 *  "demo to paid conversion" and "wallet-dropped-then-retried" funnels. */
export function trackPlayButtonClicked(): void {
  const session = getSession();
  const next_count = (session.playClickCount ?? 0) + 1;
  setSession({ playClickCount: next_count });
  const after_demo = !!session.demoStartedAt;
  const ms_since_demo = session.demoStartedAt ? Date.now() - session.demoStartedAt : null;
  track('play_button_clicked', {
    after_demo,
    ms_since_demo,
    play_click_count: next_count,
  });
}

/** Fired when a PAID match successfully starts (post payment + onJoin).
 *  Different from `play_button_clicked` — that's the intent, this is
 *  the actual completed funnel. The gap between the two is the wallet
 *  + payment drop-off. */
export function trackPaidPlayStarted(props: Record<string, any> = {}): void {
  const session = getSession();
  const next_count = (session.paidPlayCount ?? 0) + 1;
  setSession({ paidPlayCount: next_count });
  const after_demo = !!session.demoStartedAt;
  track('paid_play_started', {
    ...props,
    after_demo,
    paid_play_count: next_count,
  });
}
